// Shared route definitions used by all modules (Industrial, Monitoring, Manager).
// Module-specific `protected.jsx` files compose these with module-only routes.
//
// Splitting the route tree this way removes ~600 lines of duplication.

import { Navigate } from 'react-router';

import { isIndustrial } from 'constants/module.constant';

// ---- Reports (shared by all modules) ---------------------------------

export const reportsRoutes = {
  path: 'reports',
  children: [
    {
      index: true,
      element: (
        <Navigate
          to={isIndustrial ? '/reports/seasonal-analysis' : '/reports/daily-profile'}
          replace
        />
      ),
    },
    {
      path: 'analytics',
      lazy: async () => ({
        Component: (await import('app/pages/shared/reports/DataAnalytics'))
          .default,
      }),
    },
    {
      path: 'daily-profile',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/reports/DailyProfile/DailyProfiles')
        ).default,
      }),
    },
    {
      path: 'weather',
      lazy: async () => ({
        Component: (
          await import(
            'app/pages/shared/reports/WeatherAnalysis/WeatherAnalysis'
          )
        ).default,
      }),
    },
    {
      path: 'annual',
      lazy: async () => ({
        Component: (await import('app/pages/shared/reports/AnnualQI/AnnualAqi'))
          .default,
      }),
    },
    {
      path: 'seasonal-analysis',
      lazy: async () => ({
        Component: (
          await import(
            'app/pages/shared/reports/SeasonalAnalysis/SeasonalAnalysisPage'
          )
        ).default,
      }),
    },
    {
      path: 'periodic-analysis',
      lazy: async () => ({
        Component: (
          await import(
            'app/pages/shared/reports/PeriodicAnalysis/PeriodicAnalysis'
          )
        ).default,
      }),
    },
    {
      path: 'correlation-analysis',
      lazy: async () => ({
        Component: (
          await import(
            'app/pages/shared/reports/CorrelationAnalysis/CorrelationAnalysisPage'
          )
        ).default,
      }),
    },
  ],
};

// ---- Alerts (shared by Industrial + Monitoring) ----------------------

export const alertsRoutes = {
  path: 'alerts',
  children: [
    {
      index: true,
      element: <Navigate to="/alerts/alert-center" replace />,
    },
    {
      path: 'alert-center',
      lazy: async () => ({
        Component: (await import('app/pages/shared/alerts/AlertCenterContent'))
          .default,
      }),
    },
    {
      path: 'my-rules',
      lazy: async () => ({
        Component: (await import('app/pages/shared/alerts/MyAlertRulesContent'))
          .default,
      }),
    },
    {
      path: 'my-rules/create',
      lazy: async () => ({
        Component: (await import('app/pages/shared/alerts/AlertRuleFormPage'))
          .default,
      }),
    },
    {
      path: 'my-rules/:ruleId/edit',
      lazy: async () => ({
        Component: (await import('app/pages/shared/alerts/AlertRuleFormPage'))
          .default,
      }),
    },
    {
      path: 'settings',
      lazy: async () => ({
        Component: (await import('app/pages/shared/alerts/SettingsContent'))
          .default,
      }),
    },
  ],
};

export const alertDetailsRoute = {
  path: 'alert-details/:alertId',
  lazy: async () => ({
    Component: (await import('app/pages/shared/alerts/AlertDetail')).default,
  }),
};

// ---- Device management (shared by Industrial + Monitoring) -----------

export const deviceManagementRoutes = {
  path: 'device-management',
  children: [
    {
      index: true,
      element: <Navigate to="/device-management/overview" replace />,
    },
    {
      path: 'overview',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/Overview')).default,
      }),
    },
    {
      path: 'stations',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/Informations'))
          .default,
      }),
    },
    {
      path: 'settings',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/Settings')).default,
      }),
    },
    {
      path: 'calibration',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/devices/CalibrationOverview')
        ).default,
      }),
    },
    {
      path: 'thresholds',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/ThresholdSettings'))
          .default,
      }),
    },
    {
      path: 'choose-integration',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/devices/ChooseIntegrationMethod')
        ).default,
      }),
    },
    {
      path: 'quick-setup',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/QuickProviderSetup'))
          .default,
      }),
    },
    {
      path: 'create',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/CreateStation'))
          .default,
      }),
    },
    {
      path: 'edit/:stationId',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/EditStation'))
          .default,
      }),
    },
    {
      path: 'station/:stationId',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/StationDetails'))
          .default,
      }),
    },
    {
      path: 'station/:stationId/calibrations/:calibrationId',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/CalibrationDetails'))
          .default,
      }),
    },
    {
      path: 'calibration/add',
      lazy: async () => ({
        Component: (await import('app/pages/shared/devices/Calibration'))
          .default,
      }),
    },
    {
      path: 'zwinsoft/register',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/devices/ZwinsoftRegistration')
        ).default,
      }),
    },
  ],
};

// Legacy /devices/* path that simply redirects into /device-management/*.
export const devicesLegacyRedirects = {
  path: 'devices',
  children: [
    {
      index: true,
      element: <Navigate to="/device-management/stations" replace />,
    },
    {
      path: 'information',
      element: <Navigate to="/device-management/stations" replace />,
    },
    {
      path: 'create',
      element: <Navigate to="/device-management/create" replace />,
    },
    {
      path: 'edit/:stationId',
      element: <Navigate to="/device-management/edit/:stationId" replace />,
    },
    {
      path: 'station/:stationId',
      element: (
        <Navigate to="/device-management/station/:stationId" replace />
      ),
    },
    {
      path: 'calibration',
      element: <Navigate to="/device-management/calibration" replace />,
    },
  ],
};

// ---- App settings (shared by all modules) ----------------------------

export const settingsRoutes = {
  path: 'settings',
  lazy: async () => ({
    Component: (await import('app/pages/shared/settings/Layout')).default,
  }),
  children: [
    {
      index: true,
      element: <Navigate to="/settings/general" replace />,
    },
    {
      path: 'general',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/settings/sections/General')
        ).default,
      }),
    },
    {
      path: 'appearance',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/settings/sections/Appearance')
        ).default,
      }),
    },
    {
      path: 'configurations',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/settings/sections/Configurations')
        ).default,
      }),
    },
    {
      path: 'tenant',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/settings/sections/TenantSettings')
        ).default,
      }),
    },
  ],
};

// ---- Common single-route shortcuts -----------------------------------

export const dashboardRoute = {
  path: 'dashboard',
  lazy: async () => ({
    Component: (await import('app/pages/shared/dashboards/home')).default,
  }),
};

export const helpRoute = {
  path: 'help-center',
  lazy: async () => ({
    Component: (await import('app/pages/shared/help/helpPage')).default,
  }),
};
